import type { FormArray, FormControl, FormGroup } from '@angular/forms';
import type { Id } from '@common/types';
import type { CrmPipeline, CrmPipelineSetting, CrmPipelineStep } from '@integrations/crm-pipelines';
import type { FormElement } from '@common/types/form-element.type';
import type { FormElementFormGroup } from '@common/types/form-element-forms.type';
import type { PaymentElement } from '@integrations/payment-methods';
import type { PaymentElementFormGroup } from '@common/types/payment-element.form';
import type {
    FormAdditionalSettings,
    FormAdditionalSettingsFormGroupType,
} from '@components/sp-form-additional-settings';
import type { FormButtonSettings, FormButtonSettingsFormGroupType } from '@components/sp-form-button-settings';
import type { FormInputSettings, FormInputSettingsFormGroupType } from '@components/sp-form-input-settings';
import { type AnalyticsSettings, type AnalyticsSettingsFormGroupType } from '@common/types/analytics-setings.type';
import { type EPaymentView } from '@common/enums/payment-form-view.enum';
import type { FormWidgetData } from '@web/widgets/form/type';

export type PaymentFormWidgetData = {
    selectedAddressBook: number;
    selectedPipeline: CrmPipeline;
    selectedPipelineStep: CrmPipelineStep;
    selectedPipelineSettings: CrmPipelineSetting[];
    createAddressBook: boolean;
    elements: FormElement[];
    dealName: string;
    inputSettings: FormInputSettings;
    formButtonSettings: FormButtonSettings;
    additionalFormSettings: FormAdditionalSettings;
    addCrmTags: boolean;
    contactTags: number[];
    addAddressBookTags: boolean;
    contactAddressBookTags: number[];
    paymentFormName: string;
    productType: ProductType;
    productName: string;
    productSettings: ProductSettings;
    isShowModal: boolean;
    paymentElements: PaymentElement[];
    analyticsSettings?: AnalyticsSettings;
    modalTextHint: string;
    formView: EPaymentView;
    isSynchronizeStyle: boolean;
    hideOnMobile?: boolean;
    hideOnTablet?: boolean;
    hideOnDesktop?: boolean;
    position: {
        width?: number;
        tabletWidth?: number;
        mobileWidth?: number;

        x?: number;
        tabletX?: number;
        mobileX?: number;

        y?: number;
        tabletY?: number;
        mobileY?: number;

        rotate?: number;
        tabletRotate?: number;
        mobileRotate?: number;

        zIndex?: number;
        tabletZIndex?: number;
        mobileZIndex?: number;
    };

    /** @deprecated */
    subscriptionMessage?: string;
};

export type PaymentWidgetDataFormType = {
    selectedAddressBook: FormControl<PaymentFormWidgetData['selectedAddressBook']>;
    selectedPipeline: FormControl<PaymentFormWidgetData['selectedPipeline']>;
    selectedPipelineStep: FormControl<PaymentFormWidgetData['selectedPipelineStep']>;
    selectedPipelineSettings: FormControl<PaymentFormWidgetData['selectedPipelineSettings']>;
    createAddressBook: FormControl<PaymentFormWidgetData['createAddressBook']>;
    elements: FormArray<FormElementFormGroup>;
    paymentElements: FormArray<PaymentElementFormGroup>;
    subscriptionMessage: FormControl<PaymentFormWidgetData['subscriptionMessage']>;
    dealName: FormControl<PaymentFormWidgetData['dealName']>;
    paymentFormName: FormControl<PaymentFormWidgetData['paymentFormName']>;
    productType: FormControl<PaymentFormWidgetData['productType']>;
    productName: FormControl<PaymentFormWidgetData['productName']>;
    productSettings: FormControl<PaymentFormWidgetData['productSettings']>;
    isShowModal: FormControl<PaymentFormWidgetData['isShowModal']>;
    inputSettings: FormInputSettingsFormGroupType;
    addCrmTags: FormControl<PaymentFormWidgetData['addCrmTags']>;
    contactTags: FormArray<FormControl<number>>;
    formButtonSettings: FormButtonSettingsFormGroupType;
    additionalFormSettings: FormAdditionalSettingsFormGroupType;
    analyticsSettings: AnalyticsSettingsFormGroupType;
    modalTextHint: FormControl<PaymentFormWidgetData['modalTextHint']>;
    formView: FormControl<PaymentFormWidgetData['formView']>;
    isSynchronizeStyle: FormControl<PaymentFormWidgetData['isSynchronizeStyle']>;
    hideOnMobile: FormControl<PaymentFormWidgetData['hideOnMobile']>;
    hideOnTablet: FormControl<PaymentFormWidgetData['hideOnTablet']>;
    hideOnDesktop: FormControl<PaymentFormWidgetData['hideOnDesktop']>;
    position: FormGroup<{
        width: FormControl<PaymentFormWidgetData['position']['width']>;
        tabletWidth: FormControl<PaymentFormWidgetData['position']['tabletWidth']>;
        mobileWidth: FormControl<PaymentFormWidgetData['position']['mobileWidth']>;
        x: FormControl<PaymentFormWidgetData['position']['x']>;
        tabletX: FormControl<PaymentFormWidgetData['position']['tabletX']>;
        mobileX: FormControl<PaymentFormWidgetData['position']['mobileX']>;
        y: FormControl<PaymentFormWidgetData['position']['y']>;
        tabletY: FormControl<PaymentFormWidgetData['position']['tabletY']>;
        mobileY: FormControl<PaymentFormWidgetData['position']['mobileY']>;
        rotate: FormControl<PaymentFormWidgetData['position']['rotate']>;
        tabletRotate: FormControl<PaymentFormWidgetData['position']['tabletRotate']>;
        mobileRotate: FormControl<PaymentFormWidgetData['position']['mobileRotate']>;
        zIndex: FormControl<PaymentFormWidgetData['position']['zIndex']>;
        tabletZIndex: FormControl<PaymentFormWidgetData['position']['tabletZIndex']>;
        mobileZIndex: FormControl<PaymentFormWidgetData['position']['mobileZIndex']>;
    }>;
    addAddressBookTags: FormControl<FormWidgetData['addAddressBookTags']>;
    contactAddressBookTags: FormArray<FormControl<number>>;
};

export type ProductSettings = EduCourseProductSettings;

export type EduCourseProductSettings = {
    courseId: Id;
    courseTariffId: Id | undefined;
    isPaid?: boolean;
};

export enum ProductType {
    default = 'default',
    eduCourse = 'eduCourse',
}
